import React, { useEffect, useState } from "react";
import axios from "axios";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import SpinLoading from "components/Loading/SpinLoading.js";
import intl from "react-intl-universal";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import MaterialTable from "@material-table/core";

import {
  createTheme,
  MuiThemeProvider,
} from "@material-ui/core/styles";

import Reading from "spectral-core/models/Reading";

import {
  useRootSelector,
  useRootDispatch,
} from "hooks/reduxHooks";

import { logoutAction } from "store/user/user.actions";

import { useHistory } from "react-router-dom";

import {
  getModels,
  saveRunModelsBatch,
  RunModelsBatch,
  UpdateResults,
} from "spectral-core/api";

interface Props {
  open: boolean;
  onClose: () => void;
  selectedReadings: Reading[];
}

const desiredResultOrder = [
  "N",
  "P",
  "K",
  "Ca",
  "Mg",
  "S",
  "B",
  "Cu",
  "Fe",
  "Mn",
  "Zn",
];

export default function RecalculateReadingsModal({
  open,
  onClose,
  selectedReadings,
}: Props) {
  const accessToken = useRootSelector(
    (state) => state.user.currentToken?.accessToken
  );

  const dispatch = useRootDispatch();

  const history = useHistory();

  const [loading, setLoading] = useState(false);

  const [models, setModels] = useState<any[]>([]);

  const [selectedModel, setSelectedModel] = useState<any | null>(
    null
  );

  const [recalculating, setRecalculating] = useState(false);

  const [comparisonRows, setComparisonRows] = useState<any[]>([]);

  const [comparisonGroups, setComparisonGroups] =  useState<any[]>([]);

  const [selectedComparisonTab, setSelectedComparisonTab] =  useState(0);

  const [recalculatedReadings, setRecalculatedReadings] = useState<Reading[]>([]);

  const user = useRootSelector((state) => state.user.currentUser?.name);

  const getModelCultureType = (model: any): number | null => {
    try {
      const modelInfo = JSON.parse(model.model);

      return modelInfo?.CultureType ?? null;
    } catch {
      return null;
    }
  };

  interface PredictedValue {
  ParameterName: string;
  Value: number;
  ReferenceValue?: number;
}

const parsePredictedValues = (
  predictedValues: string
): PredictedValue[] => {
  try {
    return JSON.parse(predictedValues ?? "[]");
  } catch {
    return [];
  }
};

  const theme = createTheme({
    palette: {
      primary: {
        main: "#00bcd4",
      },
      secondary: {
        main: "#00bcd4",
      },
    },
  });

  useEffect(() => {
    async function loadModels() {
      if (!open) return;

      if (!accessToken) return;

      setLoading(true);

      try {
        const apiModels = await getModels(accessToken);

        setModels(apiModels);
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          (err.response?.status === 401 ||
            err.response?.status === 403)
        ) {
          dispatch(logoutAction());

          history.push("/");
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    }

    loadModels();
  }, [open, accessToken, dispatch, history]);

  const selectedReadingTypes = selectedReadings
    .map((r) => r.type)
    .filter(Boolean);

  const selectedCulture = selectedReadings[0]?.culture;

  const filteredModels = models
    .filter((model, index, self) => {
      const cultureType =
        getModelCultureType(model);

      const isCompatibleCulture =
        selectedCulture === 5 ||
        cultureType === selectedCulture;

      return (
        model.applicationName &&
        isCompatibleCulture &&
        index ===
          self.findIndex(
            (m) =>
              m.applicationName ===
              model.applicationName
          )
      );
    })
    .sort((a, b) =>
      a.applicationName.localeCompare(
        b.applicationName
      )
    );

  const handleRecalculate = async () => {
    if (!accessToken) return;

    if (!selectedModel) return;

    if (selectedReadings.length === 0) return;

    const rowKeys = selectedReadings
      .map((r) => r.rowKey)
      .filter(Boolean);

    try {
      setRecalculating(true);

      const response = await RunModelsBatch(
      accessToken,
      selectedModel.applicationName,
      rowKeys
    );

    const recalculated = response.data ?? [];

    setRecalculatedReadings(recalculated);

    const groups: any[] = [];

  for (const originalReading of selectedReadings) {
    const recalculatedReading = recalculated.find(
      (r: Reading) => r.rowKey === originalReading.rowKey
    );

  if (!recalculatedReading) continue;

  const originalPredicted = parsePredictedValues(
    originalReading.predictedValues
  );

  const recalculatedPredicted = parsePredictedValues(
    recalculatedReading.predictedValues
  );

  const rows: any[] = [];

  for (const originalValue of originalPredicted) {
    const recalculatedValue =
      recalculatedPredicted.find(
        (p) =>
          p.ParameterName?.toLowerCase() ===
          originalValue.ParameterName?.toLowerCase()
      );

    rows.push({
      parameterName:
        originalValue.ParameterName,

      currentValue:
        Number(originalValue.Value?.toFixed(2)),

      labValue:
        originalValue.ReferenceValue != null
          ? Number(originalValue.ReferenceValue.toFixed(2))
          : "-",

      recalculatedValue:
        recalculatedValue?.Value != null
          ? recalculatedValue.Value.toFixed(2)
          : "",
    });
  }

  rows.sort((a, b) => {
  const indexA = desiredResultOrder.indexOf(a.parameterName);
  const indexB = desiredResultOrder.indexOf(b.parameterName);

  return (
    (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
    (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
  );
  });

  groups.push({
    readingName:
      originalReading.name ??
      originalReading.rowKey,

    rowKey:
      originalReading.rowKey,

    rows,
  });
}

setComparisonGroups(groups);
    } catch (err) {
      console.error(
        "Erro ao recalcular leituras",
        err
      );

      alert(intl.get("alerts.recalculateError"));
    } finally {
      setRecalculating(false);
    }
    setSelectedComparisonTab(0);
  };

  const handleSaveRecalculation = async () => {
    if (!accessToken) return;

    try {
      setRecalculating(true);

      await UpdateResults(
        accessToken,
        user!,
        recalculatedReadings
      );

      setComparisonGroups([]);
      setSelectedComparisonTab(0);
      setRecalculatedReadings([]);
      setSelectedModel(null);

      onClose();

      alert(intl.get("alerts.recalculateSuccess"));
    } catch (err) {
      console.error(err);

      alert(intl.get("alerts.unableToSave"));
    } finally {
      setRecalculating(false);
    }
  };

  const handleSaveSingleReading = async (rowKey: string) => 
  {
    if (!accessToken) return;

    try {
      setRecalculating(true);

      const readingToSave =
        recalculatedReadings.find(
          (r) => r.rowKey === rowKey
        );

      if (!readingToSave) {
        alert(intl.get("alerts.readingNotFound"));
        return;
      }

      await UpdateResults(
        accessToken,
        user!,
        [readingToSave]
      );

      setComparisonGroups((prev) =>
      prev.map((g) =>
        g.rowKey === rowKey
          ? { ...g, saved: true }
          : g
      ));

      alert(intl.get("alerts.updateSuccess"));
    } catch (err) {
      console.error(err);

      alert(intl.get("alerts.updateError"));
    } finally {
      setRecalculating(false);
    }
  };

  const handleCancelRecalculation = () => {
  setComparisonGroups([]);
  setSelectedComparisonTab(0);
  setRecalculatedReadings([]);
  setSelectedModel(null);
  onClose();
  };  

  return (
  <Dialog
    open={open}
    onClose={
      recalculating ? undefined : onClose
    }
    fullWidth
    maxWidth="lg"
  >
    <DialogTitle>
      {intl.get("recalculateModal.readingsRecalculate")}
    </DialogTitle>

    <DialogContent>
      {!loading && !recalculating && (
        <>
          {comparisonGroups.length === 0 ? (
            <>
              <div style={{ marginBottom: 16 }}>
                {intl.get("recalculateModal.selectedReadings")}
                {selectedReadings.length}
              </div>

              <div style={{ marginBottom: 16 }}>
                {intl.get("recalculateModal.applicationChoose")}
              </div>
            </>
          ) : (
            <div
              style={{
                marginBottom: 16,
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              {intl.get(
                "recalculateModal.comparisonResults"
              )}
            </div>
          )}
        </>
      )}

      {loading || recalculating ? (
        <div
          style={{
            height: 400,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
          }}
        >
          <SpinLoading />

          {recalculating && (
            <div>
              <div>
                {intl.get(
                  "recalculateModal.recalculatingReadings"
                )}
              </div>
              <div>
                {intl.get("recalculateModal.await")}
              </div>
            </div>
          )}
        </div>
      ) : (
        <MuiThemeProvider theme={theme}>
          {comparisonGroups.length > 0 ? (
          <>
            <>
            <Tabs
              value={selectedComparisonTab}
              onChange={(event, newValue) =>
                setSelectedComparisonTab(newValue)
              }
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {comparisonGroups.map((group) => (
                <Tab
                  key={group.rowKey}
                  label={
                    group.saved
                      ? `✓ ${group.readingName}`
                      : group.readingName
                  }
                />
              ))}
            </Tabs>

            <div style={{ marginTop: 16 }}>
              <MaterialTable
                title={`${intl.get(
                  "recalculateModal.comparisonResults"
                )} - ${
                  comparisonGroups[selectedComparisonTab]
                    ?.readingName ?? ""
                }`}
                columns={[
                  {
                    title: intl.get(
                      "recalculateModal.parameter"
                    ),
                    field: "parameterName",
                    filtering: false,
                  },
                  {
                    title: intl.get(
                      "recalculateModal.currentValue"
                    ),
                    field: "currentValue",
                    filtering: false,
                  },
                  {
                    title: intl.get(
                      "recalculateModal.labValue"
                    ),
                    field: "labValue",
                    filtering: false,
                  },
                  {
                    title: intl.get(
                      "recalculateModal.recalculatedValue"
                    ),
                    field: "recalculatedValue",
                    filtering: false,
                  },
                ]}
                data={
                  comparisonGroups[selectedComparisonTab]
                    ?.rows ?? []
                }
                options={{
                  search: false,
                  filtering: false,
                  toolbar: false,
                  grouping: false,
                  paging: false,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 18,
                marginTop: 16,
              }}
            >
              <Button
                color="primary"
                variant="contained"
                disabled={recalculating}
                onClick={() =>
                  handleSaveSingleReading(
                    comparisonGroups[selectedComparisonTab]
                      .rowKey
                  )
                }
              >
                {intl.get("recalculateModal.saveOnlyOne")}
              </Button>
            </div>
          </>
          </>
          ) : (
            <MaterialTable
              title={intl.get(
                "recalculateModal.applications"
              )}
              columns={[
                {
                  field: "applicationName",
                },
              ]}
              data={filteredModels}
              onRowClick={(
                event,
                rowData: any
              ) => {
                setSelectedModel(rowData);
              }}
              options={
                {
                  filtering: true,
                  search: false,
                  toolbar: true,
                  grouping: false,
                  paging: true,
                  rowStyle: (
                    rowData: any
                  ) => ({
                    backgroundColor:
                      selectedModel?.rowKey ===
                      rowData.rowKey
                        ? "#a8eafe"
                        : "#FFF",
                  }),
                } as any
              }
            />
          )}
        </MuiThemeProvider>
      )}
    </DialogContent>

    <DialogActions>
      {comparisonGroups.length === 0 ? (
        <>
          <Button
            color="primary"
            variant="contained"
            disabled={
              !selectedModel || recalculating
            }
            onClick={handleRecalculate}
          >
            {recalculating
              ? intl.get(
                  "recalculateModal.recalculating"
                )
              : intl.get(
                  "recalculateModal.recalculate"
                )}
          </Button>

          <Button
            onClick={onClose}
            disabled={recalculating}
          >
            {intl.get("recalculateModal.close")}
          </Button>
        </>
      ) : (
        <>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSaveRecalculation}
            disabled={recalculating}
          >
            {intl.get(
              "recalculateModal.saveAllResults"
            )}
          </Button>

          <Button
            onClick={handleCancelRecalculation}
            disabled={recalculating}
          >
            {intl.get("recalculateModal.cancel")}
          </Button>
        </>
      )}
    </DialogActions>
  </Dialog>
);
}